/**
 * @fileoverview Pluggable transport layer for the WB32COSIGN MPC relay.
 *
 * A framework-agnostic port of WINBIT32's `cosignTransports`, trimmed to the
 * transports Secresea's online initiator needs and with `fetch` injected
 * (rather than a global) so the module stays host-portable and testable.
 *
 * Each transport moves an already-encrypted opaque body between two roles
 * (`init` / `cosign`); the relay/MPC layer above is responsible for crypto.
 *
 * Included:
 *   - BroadcastChannelTransport — same-origin / same-device (zero config).
 *   - HttpRelayTransport        — cross-device via any WB32COSIGN relay
 *                                 (Secresea's API mounts one at /api/cosign).
 *   - WebRTCTransport           — peer-to-peer data channel, signalled over the
 *                                 relay; low-latency once the P2P link is up.
 *   - MultiTransport            — fan-out send, merge receive.
 *
 * Deferred (airgap copy-paste only; not required online): ManualTransport.
 */

// ── Interface ────────────────────────────────────────────────────────

export interface CosignTransport {
	readonly name: string;
	send(from: string, to: string, encryptedBody: string): Promise<void>;
	receive(myRole: string): Promise<string[]>;
	close(): void;
}

// ── BroadcastChannel (same-origin, zero config) ──────────────────────

export class BroadcastChannelTransport implements CosignTransport {
	readonly name = 'BroadcastChannel (local)';
	private channel: BroadcastChannel;
	private inbox: Map<string, string[]> = new Map();

	constructor(sessionId: string) {
		this.channel = new BroadcastChannel(`wb32cosign-${sessionId}`);
		this.channel.onmessage = (ev: MessageEvent) => {
			const { to, body } = ev.data || {};
			if (!to || !body) return;
			const queue = this.inbox.get(to) || [];
			queue.push(body);
			this.inbox.set(to, queue);
		};
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		this.channel.postMessage({ from, to, body: encryptedBody });
	}

	async receive(myRole: string): Promise<string[]> {
		const queue = this.inbox.get(myRole) || [];
		if (queue.length === 0) return [];
		this.inbox.set(myRole, []);
		return queue;
	}

	close(): void {
		this.channel.close();
	}
}

// ── HTTP relay (cross-device) ────────────────────────────────────────

const RELAY_START_RETRIES = 2;
const RATE_LIMIT_INITIAL_DELAY_MS = 2_000;
const RATE_LIMIT_MAX_DELAY_MS = 30_000;

/** Parse an integer `Retry-After` (seconds), clamped so a bad server can't stall us. */
const parseRetryAfterSeconds = (header: string | null): number | null => {
	if (!header) return null;
	const n = parseInt(header.trim(), 10);
	if (!Number.isFinite(n) || n < 0) return null;
	return Math.min(n, RATE_LIMIT_MAX_DELAY_MS / 1000);
};

/** Exponential "do not call before" window shared across one relay origin. */
class RateLimitBackoff {
	private notBeforeMs = 0;
	private nextDelayMs = RATE_LIMIT_INITIAL_DELAY_MS;

	delayBeforeNext(): number {
		const remaining = this.notBeforeMs - Date.now();
		return remaining > 0 ? remaining : 0;
	}

	noteRateLimited(retryAfterSeconds: number | null): void {
		const explicitMs = retryAfterSeconds !== null ? retryAfterSeconds * 1000 : this.nextDelayMs;
		this.notBeforeMs = Date.now() + explicitMs;
		this.nextDelayMs = Math.min(this.nextDelayMs * 2, RATE_LIMIT_MAX_DELAY_MS);
	}

	noteOk(): void {
		this.notBeforeMs = 0;
		this.nextDelayMs = RATE_LIMIT_INITIAL_DELAY_MS;
	}
}

const sleepForBackoff = async (backoff: RateLimitBackoff): Promise<void> => {
	const wait = backoff.delayBeforeNext();
	if (wait > 0) await new Promise((r) => setTimeout(r, wait));
};

export class HttpRelayTransport implements CosignTransport {
	readonly name: string;
	private relayUrl: string;
	private sessionId: string;
	private fetchImpl: typeof fetch;
	private started = false;
	private backoff = new RateLimitBackoff();

	constructor(relayUrl: string, sessionId: string, fetchImpl: typeof fetch) {
		this.relayUrl = relayUrl.replace(/\/+$/, '');
		this.sessionId = sessionId;
		this.fetchImpl = fetchImpl;
		this.name = `HTTP relay (${this.relayUrl.replace(/^https?:\/\//, '').split('/')[0] || 'same-origin'})`;
	}

	private async ensureSession(): Promise<void> {
		if (this.started) return;
		for (let attempt = 0; attempt <= RELAY_START_RETRIES; attempt++) {
			await sleepForBackoff(this.backoff);
			try {
				const resp = await this.fetchImpl(`${this.relayUrl}/${this.sessionId}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify([]),
				});
				if (resp.ok || resp.status === 201 || resp.status === 409) {
					this.started = true;
					this.backoff.noteOk();
					return;
				}
				if (resp.status === 429) {
					this.backoff.noteRateLimited(parseRetryAfterSeconds(resp.headers.get('Retry-After')));
					continue;
				}
			} catch (err) {
				console.warn(`[cosign/relay] ensureSession attempt ${attempt}:`, (err as Error)?.message);
			}
		}
		this.started = true;
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		await this.ensureSession();
		await sleepForBackoff(this.backoff);
		const response = await this.fetchImpl(`${this.relayUrl}/message/${this.sessionId}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ session_id: this.sessionId, from, to: [to], body: encryptedBody }),
		});
		if (response.status === 429) {
			this.backoff.noteRateLimited(parseRetryAfterSeconds(response.headers.get('Retry-After')));
			return; // soft failure — MPC layer retries on its next round-trip
		}
		if (!response.ok) {
			const text = await response.text().catch(() => '');
			throw new Error(`Relay send failed: ${response.status} ${text}`);
		}
		this.backoff.noteOk();
	}

	async receive(myRole: string): Promise<string[]> {
		await this.ensureSession();
		await sleepForBackoff(this.backoff);
		const response = await this.fetchImpl(
			`${this.relayUrl}/message/${this.sessionId}/${encodeURIComponent(myRole)}`,
			{ method: 'GET' },
		);
		if (response.status === 429) {
			this.backoff.noteRateLimited(parseRetryAfterSeconds(response.headers.get('Retry-After')));
			return [];
		}
		if (!response.ok) {
			if (response.status === 404) return [];
			throw new Error(`Relay receive failed: ${response.status}`);
		}
		this.backoff.noteOk();
		return response.json();
	}

	close(): void {
		/* nothing to clean up */
	}
}

// ── Multi-transport (fan-out send, merge receive) ────────────────────

export class MultiTransport implements CosignTransport {
	readonly name: string;
	private transports: CosignTransport[];

	constructor(transports: CosignTransport[]) {
		this.transports = transports;
		this.name = transports.map((t) => t.name).join(' + ');
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		await Promise.allSettled(this.transports.map((t) => t.send(from, to, encryptedBody)));
	}

	async receive(myRole: string): Promise<string[]> {
		const settled = await Promise.allSettled(this.transports.map((t) => t.receive(myRole)));
		const all: string[] = [];
		for (const r of settled) if (r.status === 'fulfilled') all.push(...r.value);
		return all;
	}

	close(): void {
		for (const t of this.transports) t.close();
	}
}

// ── WebRTC (peer-to-peer data channel, relay-signalled) ──────────────

export type CosignRole = 'init' | 'cosign';

const SIGNAL_POLL_MS = 800;
const ICE_SERVERS: RTCIceServer[] = [
	{ urls: 'stun:stun.l.google.com:19302' },
	{ urls: 'stun:stun1.l.google.com:19302' },
];

interface SignalEnvelope {
	type: 'offer' | 'answer' | 'candidate';
	sdp?: string;
	candidate?: RTCIceCandidateInit;
}

/**
 * Direct peer-to-peer transport over an `RTCDataChannel`. The offer/answer/ICE
 * handshake is signalled through the same WB32COSIGN relay (distinct
 * `signal-init`/`signal-cosign` roles) so no extra server is needed. `send()`
 * is fail-fast — it silently drops while the channel isn't open — so this is
 * safe to combine with {@link HttpRelayTransport} inside a {@link MultiTransport}:
 * the relay is the always-on fabric, WebRTC opportunistically short-circuits it.
 *
 * Ported from WINBIT32's transport with `fetch` injected (Secresea keeps the
 * module host-portable). Only meaningful in a browser with `RTCPeerConnection`;
 * callers must guard creation in non-DOM environments.
 */
export class WebRTCTransport implements CosignTransport {
	readonly name = 'WebRTC (peer-to-peer)';
	private pc: RTCPeerConnection;
	private dc: RTCDataChannel | null = null;
	private inbox: Map<string, string[]> = new Map();
	private relayUrl: string;
	private sessionId: string;
	private fetchImpl: typeof fetch;
	private myRole: CosignRole;
	private peerRole: CosignRole;
	private closed = false;
	private signalPollTimer: ReturnType<typeof setInterval> | null = null;
	private iceCandidateQueue: RTCIceCandidateInit[] = [];
	private remoteDescSet = false;
	private signalBackoff = new RateLimitBackoff();
	private onOpen?: () => void;

	constructor(
		sessionId: string,
		relayUrl: string,
		fetchImpl: typeof fetch,
		myRole: CosignRole,
		opts?: { onOpen?: () => void },
	) {
		this.sessionId = sessionId;
		this.relayUrl = relayUrl.replace(/\/+$/, '');
		this.fetchImpl = fetchImpl;
		this.myRole = myRole;
		this.peerRole = myRole === 'init' ? 'cosign' : 'init';
		this.onOpen = opts?.onOpen;

		this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

		this.pc.onicecandidate = (ev) => {
			if (ev.candidate) {
				void this.postSignal({ type: 'candidate', candidate: ev.candidate.toJSON() });
			}
		};

		if (myRole === 'init') {
			this.dc = this.pc.createDataChannel('mpc', { ordered: true });
			this.setupDataChannel(this.dc);
			void this.startOffer();
		} else {
			this.pc.ondatachannel = (ev) => {
				this.dc = ev.channel;
				this.setupDataChannel(this.dc);
			};
			this.pollSignals();
		}
	}

	private setupDataChannel(dc: RTCDataChannel): void {
		dc.onopen = () => {
			if (this.signalPollTimer) { clearInterval(this.signalPollTimer); this.signalPollTimer = null; }
			try { this.onOpen?.(); } catch { /* listener errors shouldn't break the channel */ }
		};
		dc.onmessage = (ev) => {
			try {
				const { to, body } = JSON.parse(ev.data) || {};
				if (to && body) {
					const queue = this.inbox.get(to) || [];
					queue.push(body);
					this.inbox.set(to, queue);
				}
			} catch { /* skip malformed */ }
		};
		dc.onclose = () => { this.closed = true; };
		dc.onerror = (err) => { console.warn(`[cosign/webrtc] data channel error (${this.myRole}):`, err); };
	}

	private async startOffer(): Promise<void> {
		const offer = await this.pc.createOffer();
		await this.pc.setLocalDescription(offer);
		await this.postSignal({ type: 'offer', sdp: offer.sdp });
		this.pollSignals();
	}

	private async postSignal(envelope: SignalEnvelope): Promise<void> {
		try {
			await this.fetchImpl(`${this.relayUrl}/message/${this.sessionId}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					session_id: this.sessionId,
					from: `signal-${this.myRole}`,
					to: [`signal-${this.peerRole}`],
					body: JSON.stringify(envelope),
				}),
			});
		} catch (err) {
			console.warn('[cosign/webrtc] signal post failed:', (err as Error)?.message);
		}
	}

	private pollSignals(): void {
		if (this.signalPollTimer) return;
		this.signalPollTimer = setInterval(() => void this.fetchSignals(), SIGNAL_POLL_MS);
		void this.fetchSignals();
	}

	private async fetchSignals(): Promise<void> {
		if (this.closed) {
			if (this.signalPollTimer) { clearInterval(this.signalPollTimer); this.signalPollTimer = null; }
			return;
		}
		// Honour any active 429 cooldown so an un-establishable P2P link can't
		// hammer the relay's signal endpoint.
		if (this.signalBackoff.delayBeforeNext() > 0) return;
		try {
			const resp = await this.fetchImpl(`${this.relayUrl}/message/${this.sessionId}/signal-${this.myRole}`, { method: 'GET' });
			if (resp.status === 429) {
				this.signalBackoff.noteRateLimited(parseRetryAfterSeconds(resp.headers.get('Retry-After')));
				return;
			}
			if (!resp.ok) return;
			this.signalBackoff.noteOk();
			const messages: string[] = await resp.json();
			for (const raw of messages) {
				await this.handleSignal(JSON.parse(raw) as SignalEnvelope);
			}
		} catch { /* network error — retry next tick */ }
	}

	private async handleSignal(sig: SignalEnvelope): Promise<void> {
		if (sig.type === 'offer' && this.myRole !== 'init') {
			await this.pc.setRemoteDescription({ type: 'offer', sdp: sig.sdp });
			this.remoteDescSet = true;
			for (const c of this.iceCandidateQueue) await this.pc.addIceCandidate(c);
			this.iceCandidateQueue = [];
			const answer = await this.pc.createAnswer();
			await this.pc.setLocalDescription(answer);
			await this.postSignal({ type: 'answer', sdp: answer.sdp });
		} else if (sig.type === 'answer' && this.myRole === 'init') {
			await this.pc.setRemoteDescription({ type: 'answer', sdp: sig.sdp });
			this.remoteDescSet = true;
			for (const c of this.iceCandidateQueue) await this.pc.addIceCandidate(c);
			this.iceCandidateQueue = [];
		} else if (sig.type === 'candidate' && sig.candidate) {
			if (this.remoteDescSet) await this.pc.addIceCandidate(sig.candidate);
			else this.iceCandidateQueue.push(sig.candidate);
		}
	}

	/** True once the data channel has reached 'open'. */
	isOpen(): boolean {
		return !!this.dc && this.dc.readyState === 'open';
	}

	async send(from: string, to: string, encryptedBody: string): Promise<void> {
		// Fail-fast: never block on channel readiness so this is safe to fan out
		// alongside the HTTP relay; the relay (or a resend) delivers if P2P isn't up.
		if (!this.dc || this.dc.readyState !== 'open') return;
		try {
			this.dc.send(JSON.stringify({ from, to, body: encryptedBody }));
		} catch (err) {
			console.warn('[cosign/webrtc] send failed:', (err as Error)?.message);
		}
	}

	async receive(myRole: string): Promise<string[]> {
		const queue = this.inbox.get(myRole) || [];
		if (queue.length === 0) return [];
		this.inbox.set(myRole, []);
		return queue;
	}

	close(): void {
		this.closed = true;
		if (this.signalPollTimer) { clearInterval(this.signalPollTimer); this.signalPollTimer = null; }
		if (this.dc) { try { this.dc.close(); } catch { /* */ } }
		try { this.pc.close(); } catch { /* */ }
	}
}

/** WebRTC is only usable where the browser exposes `RTCPeerConnection`. */
export const isWebRtcSupported = (): boolean =>
	typeof RTCPeerConnection !== 'undefined';

// ── Factory ──────────────────────────────────────────────────────────

export interface DefaultTransportOptions {
	sessionId: string;
	/** When set, adds an HTTP relay leg (and enables WebRTC signalling). */
	relayBaseUrl?: string;
	/** Required when `relayBaseUrl` is set. */
	fetchImpl?: typeof fetch;
	/**
	 * This party's role. When provided (and `RTCPeerConnection` exists) a
	 * peer-to-peer WebRTC leg is added. Secresea is always the `init`iator.
	 */
	myRole?: CosignRole;
	/** Fires when the WebRTC data channel opens (UI can drop "relay only" hints). */
	onWebRtcOpen?: () => void;
}

/**
 * Build the standard transport stack: BroadcastChannel (same device), an
 * opportunistic WebRTC peer-to-peer leg (low latency once open) and an HTTP
 * relay leg (always-on fallback) when a relay URL is supplied. BroadcastChannel
 * short-circuits when both parties share an origin; WebRTC is skipped silently
 * where `RTCPeerConnection` is unavailable (SSR/tests) or `myRole` is unknown.
 */
export const createDefaultTransports = (opts: DefaultTransportOptions): CosignTransport => {
	const bc = new BroadcastChannelTransport(opts.sessionId);
	if (!opts.relayBaseUrl) return bc;
	if (!opts.fetchImpl) throw new Error('createDefaultTransports: fetchImpl is required when relayBaseUrl is set');
	const relay = new HttpRelayTransport(opts.relayBaseUrl, opts.sessionId, opts.fetchImpl);

	const legs: CosignTransport[] = [bc];
	if (opts.myRole && isWebRtcSupported()) {
		legs.push(new WebRTCTransport(opts.sessionId, opts.relayBaseUrl, opts.fetchImpl, opts.myRole, { onOpen: opts.onWebRtcOpen }));
	}
	legs.push(relay);
	return new MultiTransport(legs);
};
